import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  apiErrorText,
  apiLoadErrorText,
  useGetRequestsQuery,
  useTransitionRequestMutation,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import {
  MtoSupplySummary,
  fmtRequestDate,
  isOpenRequest,
  mtoSupplyRunsHref,
  priorityLabel,
  priorityTone,
  slaBadge,
  statusLabel,
  statusTone,
  type FunctionRequest,
} from '@entities/requests';
import { RejectDialog, RespondForm } from '@features/request-handling';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';

/** Входящие снабжения: заявки в снабжение, адресованные функции `mto`. */
const INCOMING = { box: 'incoming', function: 'mto', type: 'mto_supply' } as const;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Chip = styled.button<{ $active: boolean }>`
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme, $active }) =>
      $active ? theme.colors.primarySoft : theme.colors.surfaceHover};
    color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.primary)};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

/* Сетка карточки: на широком экране действия справа, на узком — сразу под
   заголовком, чтобы «Принять» не уезжало под таблицу материалов. */
const Card = styled.article<{ $overdue: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    'head side'
    'body side';
  gap: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(5)}`};
  padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 3px solid
    ${({ theme, $overdue }) => ($overdue ? theme.colors.status.danger : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'head'
      'side'
      'body';
    padding: ${({ theme }) => theme.spacing(4)};
  }
`;

const HeadArea = styled.div`
  grid-area: head;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  min-width: 0;
`;

const BodyArea = styled.div`
  grid-area: body;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  min-width: 0;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
`;

const Meta = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Side = styled.div`
  grid-area: side;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  align-self: start;
  gap: ${({ theme }) => theme.spacing(2)};

  @media (max-width: 720px) {
    align-items: flex-start;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};

  @media (max-width: 720px) {
    justify-content: flex-start;
  }
`;

const StrongLink = styled(Link)`
  font-size: 13.5px;
  font-weight: 600;
`;

const QuietLink = styled(Link)`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Muted = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const CountPill = styled.span`
  margin-left: auto;
  min-width: 20px;
  padding: 0 ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.primary};
  /* Цвет фона страницы: читается на фирменном красном и в светлой, и в тёмной теме. */
  color: ${({ theme }) => theme.colors.background};
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

/** Ближайший срок — выше; без срока — в конце, свежие — выше. */
function bySoonest(a: FunctionRequest, b: FunctionRequest): number {
  const da = a.due_date ?? '9999-12-31';
  const db = b.due_date ?? '9999-12-31';
  return da === db ? b.created_at.localeCompare(a.created_at) : da.localeCompare(db);
}

/** «Все»: сначала открытые (по сроку), за ними закрытые — свежие выше. */
function openFirst(a: FunctionRequest, b: FunctionRequest): number {
  const oa = isOpenRequest(a.status);
  const ob = isOpenRequest(b.status);
  if (oa !== ob) return oa ? -1 : 1;
  return oa ? bySoonest(a, b) : b.created_at.localeCompare(a.created_at);
}

/** Где по заявке есть смысл формировать ведомость: не черновик, не отменена, не возвращена. */
const RUNS_STATUSES = new Set(['submitted', 'accepted', 'in_progress', 'completed', 'closed']);

/**
 * Счётчик открытых входящих заявок для пункта рельса «Входящие заявки». Тот же
 * запрос, что у страницы, — RTK Query делит кэш, лишнего обращения нет.
 */
export function MtoRequestsRailCount() {
  const { can_read } = useAgentPermission('mto');
  const { data } = useGetRequestsQuery(INCOMING, { skip: !can_read, pollingInterval: 60_000 });
  const open = (data?.requests ?? []).filter((r) => isOpenRequest(r.status)).length;
  if (open === 0) return null;
  return (
    <CountPill role="img" aria-label={`новых и в работе: ${open}`} title="Новые и в работе">
      {open}
    </CountPill>
  );
}

/**
 * Входящие заявки снабжения (`/agents/mto/requests`): заявки в снабжение от ПТО
 * и прорабов — какие материалы нужны, по каким документам РД и к какому сроку.
 * Отсюда снабженец принимает заявку, возвращает её с причиной, берёт в работу
 * и отвечает (текст + файл), а по заявке с документами РД сразу переходит к
 * формированию ведомости материалов.
 */
export function MtoRequests() {
  const { can_write } = useAgentPermission('mto');
  const [scope, setScope] = useState<'open' | 'all'>('open');
  const { data, isLoading, error } = useGetRequestsQuery(INCOMING, { pollingInterval: 30_000 });

  const all = data?.requests ?? [];
  const open = all.filter((r) => isOpenRequest(r.status));
  const list = scope === 'open' ? open.slice().sort(bySoonest) : all.slice().sort(openFirst);

  return (
    <Page>
      <Chips role="group" aria-label="Какие заявки показать">
        <Chip
          type="button"
          $active={scope === 'open'}
          aria-pressed={scope === 'open'}
          onClick={() => setScope('open')}
        >
          Новые и в работе · {open.length}
        </Chip>
        <Chip
          type="button"
          $active={scope === 'all'}
          aria-pressed={scope === 'all'}
          onClick={() => setScope('all')}
        >
          Все · {all.length}
        </Chip>
      </Chips>

      {error ? (
        <ErrorText role="alert">
          {apiLoadErrorText(
            error,
            'Не удалось загрузить заявки.',
            'Входящие заявки видны сотрудникам снабжения.',
          )}
        </ErrorText>
      ) : isLoading ? (
        <Muted>Загрузка заявок…</Muted>
      ) : list.length === 0 ? (
        <Muted>
          {scope === 'open'
            ? 'Новых заявок нет. Когда инженер ПТО нажмёт «Передать в снабжение» в объёмах документа РД или прораб подаст заявку в разделе «Заявки», она появится здесь, а вам придёт уведомление.'
            : 'Заявок в снабжение пока не было.'}
        </Muted>
      ) : (
        list.map((r) => <IncomingCard key={r.id} request={r} canWrite={can_write} />)
      )}
    </Page>
  );
}

function IncomingCard({ request: r, canWrite }: { request: FunctionRequest; canWrite: boolean }) {
  const [transition] = useTransitionRequestMutation();
  const [moving, setMoving] = useState<'accept' | 'progress' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [responding, setResponding] = useState(false);
  const [sending, setSending] = useState(false);
  const onBusyChange = useCallback((busy: boolean) => setSending(busy), []);
  const sla = slaBadge(r);
  const runsHref = RUNS_STATUSES.has(r.status) ? mtoSupplyRunsHref(r) : null;

  async function move(action: 'accept' | 'progress', fallback: string) {
    setError(null);
    setMoving(action);
    try {
      await transition({ id: r.id, action }).unwrap();
    } catch (err) {
      setError(apiErrorText(err, fallback));
    } finally {
      setMoving(null);
    }
  }

  const closeRespond = () => {
    // Пока ответ с файлом уходит — окно не закрываем, иначе ответ потеряется.
    if (!sending) setResponding(false);
  };

  return (
    <Card $overdue={sla?.tone === 'danger'} aria-labelledby={`mto-req-${r.id}`}>
      <HeadArea>
        <Head>
          <CardTitle id={`mto-req-${r.id}`}>{r.title}</CardTitle>
          <Badge $tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
          {sla && <Badge $tone={sla.tone}>{sla.label}</Badge>}
          {r.priority !== 'normal' && (
            <Badge $tone={priorityTone(r.priority)}>{priorityLabel(r.priority)}</Badge>
          )}
        </Head>
        <Meta>поступила {fmtRequestDate(r.created_at)}</Meta>
      </HeadArea>

      <Side>
        {canWrite && r.status === 'submitted' && (
          <Actions>
            <Button
              type="button"
              $variant="primary"
              $size="sm"
              disabled={moving !== null}
              onClick={() => void move('accept', 'Заявку не удалось принять.')}
            >
              {moving === 'accept' ? 'Принимаем…' : 'Принять'}
            </Button>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              disabled={moving !== null}
              onClick={() => setRejecting(true)}
            >
              Вернуть
            </Button>
          </Actions>
        )}
        {canWrite && r.status === 'accepted' && (
          <Button
            type="button"
            $variant="primary"
            $size="sm"
            disabled={moving !== null}
            onClick={() => void move('progress', 'Заявку не удалось взять в работу.')}
          >
            {moving === 'progress' ? 'Берём в работу…' : 'Взять в работу'}
          </Button>
        )}
        {canWrite && r.status === 'in_progress' && (
          <Button type="button" $variant="primary" $size="sm" onClick={() => setResponding(true)}>
            Ответить
          </Button>
        )}
        {runsHref && <StrongLink to={runsHref}>Сформировать ведомость материалов →</StrongLink>}
        <QuietLink to={`/requests/${r.id}`}>Карточка заявки</QuietLink>
      </Side>

      <BodyArea>
        <MtoSupplySummary request={r} compact />
        {error && <ErrorText role="alert">{error}</ErrorText>}
      </BodyArea>

      {rejecting && (
        <RejectDialog
          requestId={r.id}
          title={r.title}
          placeholder="Например: не указан объект или срок, нужен список материалов с количеством"
          onClose={() => setRejecting(false)}
        />
      )}
      {responding && (
        <Modal open title="Ответ по заявке" onClose={closeRespond}>
          <RespondForm
            requestId={r.id}
            autoFocus
            onBusyChange={onBusyChange}
            onDone={() => setResponding(false)}
            onCancel={closeRespond}
          />
        </Modal>
      )}
    </Card>
  );
}
